/**
 * Document status matching backend DocumentStatus enum
 */
export type DocumentStatus =
  | 'Uploaded'
  | 'Parsing'
  | 'Parsed'
  | 'Extracting'
  | 'Completed'
  | 'PartiallyFailed'
  | 'Failed'
  | 'EmptyDocument'
  | 'CompletedWithoutEmbeddings'
  | 'AwaitingConfirmation';

export interface DocumentStatusChangedEvent {
  documentId: string;
  status: DocumentStatus;
  processedChunks: number;
  totalChunks: number;
  failedChunks: number;
  completedAt?: string | null;
  errorMessage?: string | null;
  message?: string;
  timestamp: string;
}

export interface DocumentProgressUpdate {
  documentId: string;
  stage: string;
  current: number;
  total: number;
  message?: string;
  timestamp: string;
}

export interface DocumentLogEntry {
  documentId: string;
  level: string;
  message: string;
  stage?: string;
  timestamp: string;
}

export interface DocumentErrorNotification {
  documentId: string;
  error: string;
  timestamp: string;
}

const TERMINAL_DOCUMENT_STATUSES: ReadonlySet<DocumentStatus> = new Set([
  'Completed',
  'PartiallyFailed',
  'Failed',
  'EmptyDocument',
  'CompletedWithoutEmbeddings',
]);

export function isTerminalDocumentStatus(status: DocumentStatus): boolean {
  return TERMINAL_DOCUMENT_STATUSES.has(status);
}

/**
 * Chunk status matching backend ChunkStatus enum
 */
export type ChunkStatus = 'Pending' | 'Processing' | 'Done' | 'Failed';

/**
 * Document list view DTO
 */
export interface DocumentDto {
  id: string;
  fileName: string;
  displayTitle?: string | null;
  contentType: string;
  fileSizeBytes: number;
  status: DocumentStatus;
  uploadedByUserId: string;
  uploadedByDisplayName?: string | null;
  workspaceId: string;
  domainId: string;
  sourceConnectorId?: string | null;
  sourceKind?: string | null;
  sourceLabel?: string | null;
  sourceOrigin?: string | null;
  documentType?: string | null;
  language?: string | null;
  machineModelId?: string | null;
  machineLabel?: string | null;
  machineSeries?: string | null;
  machineManufacturer?: string | null;
  machineCategory?: string | null;
  pageCount?: number | null;
  totalChunks: number;
  processedChunks: number;
  failedChunks: number;
  createdAt: string; // ISO date string
  completedAt?: string | null;
  errorMessage?: string | null;
  // Version tracking
  versionNumber: number;
  versionLabel?: string | null;
  parentDocumentId?: string | null;
  isCurrent: boolean;
}

/**
 * Document chunk DTO
 */
export interface DocumentChunkDto {
  id: string;
  chunkIndex: number;
  content: string;
  tokenCount: number;
  pageNumber?: number | null;
  status: ChunkStatus;
  retryCount: number;
  errorMessage?: string | null;
}

/**
 * Document detail view DTO with chunks
 */
export interface DocumentDetailDto extends DocumentDto {
  chunks: DocumentChunkDto[];
}

export interface DocumentFacetCountsDto {
  statuses: Record<string, number>;
  sourceKinds: Record<string, number>;
  documentTypes: Record<string, number>;
  languages: Record<string, number>;
  machines: Record<string, number>;
}

export interface DocumentMachineFacetOptionDto {
  value: string;
  label: string;
}

export interface DocumentListResponseDto {
  items: DocumentDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  availableDocumentTypes: string[];
  availableLanguages: string[];
  availableMachines: DocumentMachineFacetOptionDto[];
  facetCounts: DocumentFacetCountsDto;
}

/**
 * Document processing estimate (from Python API via C# passthrough).
 * Python sends: document_id, total_chunks, estimated_tokens, estimated_cost_usd, estimated_duration_seconds
 * C# passes through as raw object → arrives as snake_case → apiClient transforms to camelCase
 */
export interface DocumentEstimate {
  documentId?: string;
  totalChunks?: number;
  estimatedTokens?: number;
  estimatedCostUsd?: number;
  estimatedDurationSeconds?: number;
}

/**
 * Summary DTO for a single version in a document's version history
 */
export interface DocumentVersionSummaryDto {
  id: string;
  versionNumber: number;
  versionLabel?: string | null;
  status: DocumentStatus;
  fileSizeBytes: number;
  isCurrent: boolean;
  createdAt: string;
  completedAt?: string | null;
}
